import {
	annualizeFlow,
	type BalanceSheetLineValues,
	ContractSuretyAccountUnderwritingModelSize,
	Currency,
	computeAnnualizedRevenueGrowth,
	computeArToApRatio,
	computeArToEquityRatio,
	computeArToWorkingCapitalRatio,
	computeBalanceSheetWorkingCapital,
	computeBlocAvailable,
	computeCashAndEquivalents,
	computeCurrentDebt,
	computeCurrentDebtToTotalAssetsRatio,
	computeCurrentRatio,
	computeDebtToEbitdaRatio,
	computeDebtToEquityRatio,
	computeEbitda,
	computeGAndAPercentOfRevenue,
	computeGrossMarginPercent,
	computeLongTermDebt,
	computeNetMarginPercent,
	computeOperatingMarginPercent,
	computeTotalAvailableLiquidity,
	computeTotalDebt,
	computeUnderbillingsToWorkingCapitalRatio,
	computeWorkingCapitalToCurrentAssetsRatio,
	computeWorkingCapitalToTotalAssetsRatio,
	type IncomeStatementLineValues,
} from "@inrev/common";
import { BS } from "./analysis/balanceSheetRowIds";
import { IS } from "./analysis/incomeStatementRowIds";
import type { AnalysisSourceKey } from "./analysis/sources";
import { WIP } from "./analysis/wipRowIds";

/**
 * Analysis metric definitions. Each metric is computed from the *allowed* values of a period's active
 * statements, read through pluggable data sources (balance sheet, income statement, and — once wired —
 * WIP, Debt, …). A metric declares which sources it needs via {@link metric}; if any required source
 * is missing for a period, the metric resolves to "--".
 *
 * A metric returns:
 *   - `{ kind: "dollars" }` — a currency amount, rendered with the grid's currency formatting.
 *   - `{ kind: "ratio" }`   — a dimensionless ratio, rendered as "1.53x".
 *   - `{ kind: "percent" }` — a percentage, rendered as "42.0%".
 *   - `null`                — data needed for the calculation is missing; rendered as "--".
 *
 * Ratio and percent metrics carry their numeric value so the grid's P/P % change toggle can compare
 * them period-over-period; a `null` result has no numeric value and is excluded from comparison.
 *
 * Backlog metrics require the `wip` source. The WIP statement is not yet wired into the analysis grid
 * (see ./analysis/wipRowIds), so the `wip` source is empty and backlog metrics currently resolve to "--".
 */

export type AnalysisPeriodData = {
	blocSize: number | null;
	/** Factor that scales an income-statement flow measured over this period up to a full-year figure
	 *  (~1 for a full year, ~4 for a quarter). See `computeAnnualizationFactor` in `@inrev/common`. Only the two
	 *  annualized metrics use it — Annualized A/R Turnover and Annualized Revenue Growth — so a
	 *  partial-period statement doesn't distort a day count or a growth comparison against a period of
	 *  a different length. Every other metric reads its flows as reported. */
	annualizationFactor: number;
	/** Whether the period has an active statement for the given source. */
	has: (source: AnalysisSourceKey) => boolean;
	/** Allowed value for a row id within a source, or null when missing / the source is absent. */
	value: (source: AnalysisSourceKey, rowId: string) => number | null;
};

export type AnalysisMetricResult =
	| { kind: "dollars"; value: number }
	| { kind: "ratio"; value: number }
	| { kind: "percent"; value: number }
	| { kind: "days"; value: number }
	| null;

export type AnalysisMetric = {
	label: string;
	/** Human-readable formula shown in a tooltip when the row's label is hovered, written in terms of
	 *  the statement line items it reads (e.g. "Current Assets ÷ Current Liabilities"). Omitted for
	 *  metrics that are a single line item verbatim (e.g. Equity), where a formula adds nothing. */
	formula?: string;
	/** When true, a period-over-period decrease is favorable (e.g. debt, A/R turnover). Defaults to false. */
	lowerIsBetter: boolean;
	/** Underwriting model sizes whose score uses this metric; the grid marks it with a "*" only for
	 *  accounts on one of these sizes. Small accounts use no financial ratios, so an empty list is
	 *  never marked. */
	underwritingModelSizes: readonly ContractSuretyAccountUnderwritingModelSize[];
	calculate: (
		period: AnalysisPeriodData,
		priorPeriod: AnalysisPeriodData | null,
	) => AnalysisMetricResult;
};

export type AnalysisCategory = {
	key: string;
	label: string;
	metrics: readonly AnalysisMetric[];
};

// Each group lists the model sizes whose underwriting score actually reads that ratio, mapped
// strictly from calculateMediumScore/calculateLargeScore. Small accounts use no financial ratios.
const NO_MODELS: readonly ContractSuretyAccountUnderwritingModelSize[] = [];
const MEDIUM_MODEL_ONLY: readonly ContractSuretyAccountUnderwritingModelSize[] = [
	ContractSuretyAccountUnderwritingModelSize.medium,
];
const MEDIUM_AND_LARGE_MODELS: readonly ContractSuretyAccountUnderwritingModelSize[] = [
	ContractSuretyAccountUnderwritingModelSize.medium,
	ContractSuretyAccountUnderwritingModelSize.large,
];
const LARGE_MODEL_ONLY: readonly ContractSuretyAccountUnderwritingModelSize[] = [
	ContractSuretyAccountUnderwritingModelSize.large,
];

const dollars = (value: number): AnalysisMetricResult => ({ kind: "dollars", value });

// A missing line item on a present statement means $0, so numerators coalesce null to zero.
const sumMoney = (...values: (number | null)[]): number => Currency.sum(values.map((v) => v ?? 0));

// "--" means missing data only: a divide-by-zero (null denominator) has no value, but a computed
// zero is a real result and renders as "0.00x".
const xRatio = (numerator: number, denominator: number | null): AnalysisMetricResult => {
	if (denominator === null || denominator === 0) return null;
	return { kind: "ratio", value: numerator / denominator };
};

const days = (numerator: number | null, denominator: number | null): AnalysisMetricResult => {
	if (numerator === null || denominator === null || denominator === 0) return null;
	return { kind: "days", value: (numerator / denominator) * 365 };
};

const ratioResult = (value: number | null): AnalysisMetricResult =>
	value === null ? null : { kind: "ratio", value };

const percentResult = (value: number | null): AnalysisMetricResult =>
	value === null ? null : { kind: "percent", value };

// A non-summation dollar metric surfaces a single value; a missing value (null) renders as "--",
// but a real $0 renders as "$0".
const directDollars = (value: number | null): AnalysisMetricResult =>
	value === null ? null : dollars(value);

// Build a metric that resolves to "--" unless every source it reads from is present for the period.
const metric = (
	label: string,
	requires: readonly AnalysisSourceKey[],
	calculate: (p: AnalysisPeriodData, prior: AnalysisPeriodData | null) => AnalysisMetricResult,
	options?: {
		formula?: string;
		lowerIsBetter?: boolean;
		underwritingModelSizes?: readonly ContractSuretyAccountUnderwritingModelSize[];
	},
): AnalysisMetric => ({
	label,
	formula: options?.formula,
	lowerIsBetter: options?.lowerIsBetter ?? false,
	underwritingModelSizes: options?.underwritingModelSizes ?? NO_MODELS,
	calculate: (p, prior) => (requires.every((source) => p.has(source)) ? calculate(p, prior) : null),
});

// Thin readers over the generic source accessor for the two sources current metrics use.
const bsValue = (p: AnalysisPeriodData, rowId: string): number | null =>
	p.value("balanceSheet", rowId);
const isValue = (p: AnalysisPeriodData, rowId: string): number | null =>
	p.value("incomeStatement", rowId);

const balanceSheetLines = (p: AnalysisPeriodData): BalanceSheetLineValues => ({
	cash: bsValue(p, BS.cash),
	marketableSecurities: bsValue(p, BS.marketableSecurities),
	accountsReceivable: bsValue(p, BS.accountsReceivable),
	underbillings: bsValue(p, BS.underbillings),
	currentAssets: bsValue(p, BS.currentAssets),
	totalAssets: bsValue(p, BS.totalAssets),
	accountsPayable: bsValue(p, BS.accountsPayable),
	creditCardsBalance: bsValue(p, BS.creditCardsBalance),
	cashOverdraft: bsValue(p, BS.cashOverdraft),
	blocBalance: bsValue(p, BS.blocBalance),
	currentPortionOfLtd: bsValue(p, BS.currentPortionOfLtd),
	otherNotesPayable: bsValue(p, BS.otherNotesPayable),
	currentLiabilities: bsValue(p, BS.currentLiabilities),
	ltdBankTermLoan: bsValue(p, BS.ltdBankTermLoan),
	ltdRealEstate: bsValue(p, BS.ltdRealEstate),
	ltdEquipment: bsValue(p, BS.ltdEquipment),
	ltdOther: bsValue(p, BS.ltdOther),
	equity: bsValue(p, BS.equity),
});

const incomeStatementLines = (p: AnalysisPeriodData): IncomeStatementLineValues => ({
	revenue: isValue(p, IS.revenue),
	grossProfit: isValue(p, IS.grossProfit),
	operatingExpenses: isValue(p, IS.operatingExpenses),
	operatingProfit: isValue(p, IS.operatingProfit),
	netProfit: isValue(p, IS.netProfit),
	depreciationAndAmortization: isValue(p, IS.depreciationAndAmortization),
	interestExpense: isValue(p, IS.interestExpense),
	taxes: isValue(p, IS.taxes),
});

const cashAndEquivalents = (p: AnalysisPeriodData): number =>
	computeCashAndEquivalents(balanceSheetLines(p));

const workingCapital = (p: AnalysisPeriodData): number =>
	computeBalanceSheetWorkingCapital(balanceSheetLines(p));

const blocAvailable = (p: AnalysisPeriodData): number | null =>
	computeBlocAvailable(balanceSheetLines(p), p.blocSize);

const longTermDebt = (p: AnalysisPeriodData): number => computeLongTermDebt(balanceSheetLines(p));

const currentDebt = (p: AnalysisPeriodData): number => computeCurrentDebt(balanceSheetLines(p));

// Bank Debt: BLOC balance, current portion of long-term debt, and bank term loans.
const bankDebt = (p: AnalysisPeriodData): number =>
	sumMoney(
		bsValue(p, BS.blocBalance),
		bsValue(p, BS.currentPortionOfLtd),
		bsValue(p, BS.ltdBankTermLoan),
	);

// Net Cash: corporate cash net of short-term obligations (credit cards, BLOC balance, accrued
// expenses, and overbillings).
const netCash = (p: AnalysisPeriodData): number =>
	Currency.subtract(
		bsValue(p, BS.cash) ?? 0,
		sumMoney(
			bsValue(p, BS.creditCardsBalance),
			bsValue(p, BS.blocBalance),
			bsValue(p, BS.accruedExpenses),
			bsValue(p, BS.overbillings),
		),
	);

export const ANALYSIS_CATEGORIES: readonly AnalysisCategory[] = [
	{
		key: "liquidity",
		label: "Liquidity",
		metrics: [
			// Cash & Equivalents: liquidity from cash plus marketable securities
			metric("Cash & Equivalents", ["balanceSheet"], (p) => dollars(cashAndEquivalents(p)), {
				formula: "Cash + Marketable Securities",
			}),

			// Cash & Equivalents to Backlog: cash relative to project backlog cost (needs WIP)
			metric(
				"Cash & Equivalents:Backlog",
				["balanceSheet", "wip"],
				(p) => xRatio(cashAndEquivalents(p), p.value("wip", WIP.projectBacklogCost)),
				{
					formula: "(Cash + Marketable Securities) ÷ Project Backlog Cost",
					underwritingModelSizes: MEDIUM_MODEL_ONLY,
				},
			),

			// Corporate Cash to Total Assets: corporate cash as a share of total assets
			metric(
				"Corporate Cash:Total Assets",
				["balanceSheet"],
				(p) => xRatio(bsValue(p, BS.cash) ?? 0, bsValue(p, BS.totalAssets)),
				{ formula: "Cash ÷ Total Assets", underwritingModelSizes: MEDIUM_AND_LARGE_MODELS },
			),

			// Net Cash to Total Assets: cash net of short-term obligations as a share of total assets
			metric(
				"Net Cash:Total Assets",
				["balanceSheet"],
				(p) => xRatio(netCash(p), bsValue(p, BS.totalAssets)),
				{
					formula:
						"(Cash − Credit Cards − BLOC Balance − Accrued Expenses − Overbillings) ÷ Total Assets",
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),

			// BLOC Available: remaining balance on the Bank Line of Credit
			metric(
				"BLOC Available",
				["balanceSheet"],
				(p) => {
					const available = blocAvailable(p);
					return available === null ? null : dollars(available);
				},
				{ formula: "BLOC Size − BLOC Balance", underwritingModelSizes: LARGE_MODEL_ONLY },
			),

			// Total Available Liquidity: cash & equivalents plus available BLOC
			metric(
				"Total Available Liquidity",
				["balanceSheet"],
				(p) => dollars(computeTotalAvailableLiquidity(balanceSheetLines(p), p.blocSize)),
				{ formula: "Cash + Marketable Securities + (BLOC Size − BLOC Balance)" },
			),

			// Accounts Receivable Turnover: average days to collect receivables (DSO); fewer days is better
			metric(
				"Annualized A/R Turnover",
				["balanceSheet", "incomeStatement"],
				(p) =>
					days(
						bsValue(p, BS.accountsReceivable),
						annualizeFlow(isValue(p, IS.revenue), p.annualizationFactor),
					),
				{
					formula: "Accounts Receivable ÷ Annualized Revenue × 365",
					lowerIsBetter: true,
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),
		],
	},
	{
		key: "workingCapital",
		label: "Working Capital",
		metrics: [
			// Working Capital: current assets minus current liabilities
			metric("Working Capital", ["balanceSheet"], (p) => dollars(workingCapital(p)), {
				formula: "Current Assets − Current Liabilities",
			}),

			// Working Capital to Backlog: working capital relative to project backlog cost (needs WIP)
			metric(
				"WC:Backlog",
				["balanceSheet", "wip"],
				(p) => xRatio(workingCapital(p), p.value("wip", WIP.projectBacklogCost)),
				{
					formula: "Working Capital ÷ Project Backlog Cost",
					underwritingModelSizes: MEDIUM_AND_LARGE_MODELS,
				},
			),

			// Working Capital to Total Assets: working capital as a share of total assets
			metric(
				"WC:Total Assets",
				["balanceSheet"],
				(p) => ratioResult(computeWorkingCapitalToTotalAssetsRatio(balanceSheetLines(p))),
				{
					formula: "Working Capital ÷ Total Assets",
					underwritingModelSizes: MEDIUM_AND_LARGE_MODELS,
				},
			),

			// Working Capital to Current Assets: working capital as a share of current assets
			metric(
				"WC:Current Assets",
				["balanceSheet"],
				(p) => ratioResult(computeWorkingCapitalToCurrentAssetsRatio(balanceSheetLines(p))),
				{
					formula: "Working Capital ÷ Current Assets",
					underwritingModelSizes: MEDIUM_AND_LARGE_MODELS,
				},
			),

			// Current Ratio: ability to cover short-term obligations with short-term assets
			metric(
				"Current Ratio",
				["balanceSheet"],
				(p) => ratioResult(computeCurrentRatio(balanceSheetLines(p))),
				{ formula: "Current Assets ÷ Current Liabilities" },
			),

			// Accounts Receivable to Accounts Payable
			metric(
				"A/R:A/P",
				["balanceSheet"],
				(p) => ratioResult(computeArToApRatio(balanceSheetLines(p))),
				{ formula: "Accounts Receivable ÷ Accounts Payable" },
			),

			// Accounts Receivable to Working Capital
			metric(
				"A/R:WC",
				["balanceSheet"],
				(p) => ratioResult(computeArToWorkingCapitalRatio(balanceSheetLines(p))),
				{ formula: "Accounts Receivable ÷ Working Capital" },
			),

			// Underbillings to Working Capital; lower is better
			metric(
				"Underbillings:WC",
				["balanceSheet"],
				(p) => ratioResult(computeUnderbillingsToWorkingCapitalRatio(balanceSheetLines(p))),
				{
					formula: "Underbillings ÷ Working Capital",
					lowerIsBetter: true,
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),
		],
	},
	{
		key: "leverage",
		label: "Leverage",
		metrics: [
			// BLOC Balance: outstanding balance on the Bank Line of Credit; less debt is better
			metric("BLOC Balance", ["balanceSheet"], (p) => directDollars(bsValue(p, BS.blocBalance)), {
				lowerIsBetter: true,
			}),

			// Current Debt: short-term debt obligations; less debt is better
			metric("Current Debt", ["balanceSheet"], (p) => dollars(currentDebt(p)), {
				formula:
					"Credit Cards + Cash Overdraft + BLOC Balance + Current Portion of LTD + Other Notes Payable",
				lowerIsBetter: true,
			}),

			// Long Term Debt: debt obligations due beyond the next year; less debt is better
			metric("Long Term Debt", ["balanceSheet"], (p) => dollars(longTermDebt(p)), {
				formula: "Bank Term Loan + Real Estate + Equipment + Other LTD",
				lowerIsBetter: true,
			}),

			// Bank Debt: BLOC balance, current portion of long-term debt, and bank term loans; less is better
			metric("Bank Debt", ["balanceSheet"], (p) => dollars(bankDebt(p)), {
				formula: "BLOC Balance + Current Portion of LTD + Bank Term Loan",
				lowerIsBetter: true,
			}),

			// Total Debt: short-term plus long-term debt; less debt is better
			metric(
				"Total Debt",
				["balanceSheet"],
				(p) => dollars(computeTotalDebt(balanceSheetLines(p))),
				{ formula: "Current Debt + Long Term Debt", lowerIsBetter: true },
			),

			// Debt to Equity: financial leverage; lower leverage is better
			metric(
				"Debt:Equity",
				["balanceSheet"],
				(p) => ratioResult(computeDebtToEquityRatio(balanceSheetLines(p))),
				{ formula: "(Current Debt + Long Term Debt) ÷ Equity", lowerIsBetter: true },
			),

			// Debt to EBITDA: financial leverage relative to earnings
			metric(
				"Debt:EBITDA",
				["balanceSheet", "incomeStatement"],
				(p) => ratioResult(computeDebtToEbitdaRatio(balanceSheetLines(p), incomeStatementLines(p))),
				{ formula: "(Current Debt + Long Term Debt) ÷ EBITDA", lowerIsBetter: true },
			),

			// Total Liabilities to Equity: balance-sheet leverage; lower leverage is better
			metric(
				"Total Liabilities:Equity",
				["balanceSheet"],
				(p) => xRatio(bsValue(p, BS.totalLiabilities) ?? 0, bsValue(p, BS.equity)),
				{
					formula: "Total Liabilities ÷ Equity",
					lowerIsBetter: true,
					underwritingModelSizes: MEDIUM_AND_LARGE_MODELS,
				},
			),

			// Current Debt to Total Assets: short-term debt as a share of total assets; less is better
			metric(
				"Current Debt:Total Assets",
				["balanceSheet"],
				(p) => ratioResult(computeCurrentDebtToTotalAssetsRatio(balanceSheetLines(p))),
				{
					formula: "Current Debt ÷ Total Assets",
					lowerIsBetter: true,
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),

			// Bank Debt to Total Assets: bank debt as a share of total assets; less is better
			metric(
				"Bank Debt:Total Assets",
				["balanceSheet"],
				(p) => xRatio(bankDebt(p), bsValue(p, BS.totalAssets)),
				{
					formula: "Bank Debt ÷ Total Assets",
					lowerIsBetter: true,
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),
		],
	},
	{
		key: "equity",
		label: "Equity",
		metrics: [
			// Equity: the company's net worth
			metric("Equity", ["balanceSheet"], (p) => directDollars(bsValue(p, BS.equity))),

			// Accounts Receivable to Equity; lower is better
			metric(
				"A/R:Equity",
				["balanceSheet"],
				(p) => ratioResult(computeArToEquityRatio(balanceSheetLines(p))),
				{ formula: "Accounts Receivable ÷ Equity", lowerIsBetter: true },
			),

			// Equity to Backlog: equity relative to project backlog cost (needs WIP)
			metric(
				"Equity:Backlog",
				["balanceSheet", "wip"],
				(p) => xRatio(bsValue(p, BS.equity) ?? 0, p.value("wip", WIP.projectBacklogCost)),
				{
					formula: "Equity ÷ Project Backlog Cost",
					underwritingModelSizes: MEDIUM_AND_LARGE_MODELS,
				},
			),

			// G&A Expense to Equity: overhead relative to net worth; lower is better
			metric(
				"G&A:Equity",
				["balanceSheet", "incomeStatement"],
				(p) => xRatio(isValue(p, IS.operatingExpenses) ?? 0, bsValue(p, BS.equity)),
				{
					formula: "Operating Expenses ÷ Equity",
					lowerIsBetter: true,
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),

			// Backlog Gross Profit to G&A Expense: backlog profit coverage of overhead (needs WIP)
			metric(
				"Backlog Gross Profit:G&A",
				["incomeStatement", "wip"],
				(p) =>
					xRatio(
						p.value("wip", WIP.projectBacklogGrossProfit) ?? 0,
						isValue(p, IS.operatingExpenses),
					),
				{
					formula: "Project Backlog Gross Profit ÷ Operating Expenses",
					underwritingModelSizes: LARGE_MODEL_ONLY,
				},
			),
		],
	},
	{
		key: "profitability",
		label: "Profitability",
		metrics: [
			// Annualized Revenue Growth: percentage change in revenue vs. the prior period. Both periods
			// are annualized so a partial period compared against a full year measures real growth
			// rather than the difference in period length.
			metric(
				"Annualized Revenue Growth",
				["incomeStatement"],
				(p, prior) => {
					if (prior === null || !prior.has("incomeStatement")) return null;
					return percentResult(
						computeAnnualizedRevenueGrowth(
							{
								incomeStatement: incomeStatementLines(p),
								annualizationFactor: p.annualizationFactor,
							},
							{
								incomeStatement: incomeStatementLines(prior),
								annualizationFactor: prior.annualizationFactor,
							},
						),
					);
				},
				{
					formula:
						"(Annualized Revenue − Prior Annualized Revenue) ÷ Prior Annualized Revenue × 100",
				},
			),

			// Gross Margin: gross profit as a percentage of revenue
			metric(
				"Gross Margin",
				["incomeStatement"],
				(p) => percentResult(computeGrossMarginPercent(incomeStatementLines(p))),
				{ formula: "Gross Profit ÷ Revenue × 100" },
			),

			// G&A % of Revenue: general and administrative expenses as a percentage of revenue; lower is better
			metric(
				"G&A % of Revenue",
				["incomeStatement"],
				(p) => percentResult(computeGAndAPercentOfRevenue(incomeStatementLines(p))),
				{ formula: "Operating Expenses ÷ Revenue × 100", lowerIsBetter: true },
			),

			// Operating Margin: operating profit as a percentage of revenue
			metric(
				"Operating Margin",
				["incomeStatement"],
				(p) => percentResult(computeOperatingMarginPercent(incomeStatementLines(p))),
				{ formula: "Operating Profit ÷ Revenue × 100" },
			),

			// Net Margin: net profit as a percentage of revenue
			metric(
				"Net Margin",
				["incomeStatement"],
				(p) => percentResult(computeNetMarginPercent(incomeStatementLines(p))),
				{ formula: "Net Profit ÷ Revenue × 100" },
			),

			// EBITDA: earnings before interest, taxes, depreciation, and amortization
			metric(
				"EBITDA",
				["incomeStatement"],
				(p) => dollars(computeEbitda(incomeStatementLines(p))),
				{
					formula: "Operating Profit + Depreciation & Amortization + Interest Expense + Taxes",
				},
			),
		],
	},
];

/**
 * The Analysis grid's Snapshot tab: a short, curated quick-glance list rendered flat (no category
 * headers) in this exact order, drawn from {@link ANALYSIS_CATEGORIES} by label. This ordered list is
 * the single source of truth for the Snapshot set — reorder or edit it here to change the tab.
 */
export const SNAPSHOT_METRIC_LABELS: readonly string[] = [
	"Working Capital",
	"WC:Backlog",
	"Equity:Backlog",
	"Underbillings:WC",
	"Current Ratio",
	"Debt:Equity",
	"Current Debt",
	"BLOC Available",
	"Annualized A/R Turnover",
	"Net Margin",
];
